import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { formsApi, appsApi, type Form } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export function FormCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    app_id: '',
  });

  const { data: appsData } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<Form>) => formsApi.create(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      navigate(`/forms/${result.id}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.app_id) return;
    createMutation.mutate({
      name: formData.name,
      description: formData.description,
      app_id: formData.app_id,
      slug: formData.name.toLowerCase().replace(/\s+/g, '-'),
    });
  };

  const apps = appsData?.items || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/forms')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <div>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            Nouveau Formulaire
          </h2>
          <p className="text-sm text-secondary-500">
            Créez un nouveau formulaire de collecte de données
          </p>
        </div>
      </div>

      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-xl border border-secondary-200 bg-white p-6 dark:border-secondary-800 dark:bg-secondary-900">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom du formulaire *</Label>
                <Input
                  id="name"
                  placeholder="Mon Formulaire"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Description du formulaire..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="app">Application *</Label>
                <Select
                  value={formData.app_id}
                  onValueChange={(value) => setFormData({ ...formData, app_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une application" />
                  </SelectTrigger>
                  <SelectContent>
                    {apps.map((app) => (
                      <SelectItem key={app.id} value={app.id}>
                        {app.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {createMutation.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <p className="text-sm text-red-700 dark:text-red-400">
                Erreur: {(createMutation.error as Error).message}
              </p>
            </div>
          )}

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/forms')}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || !formData.name.trim() || !formData.app_id}
            >
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer le formulaire
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FormCreatePage;
